import React, {useState} from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Slider,
    Typography,
    RadioGroup,
    FormControlLabel,
    Radio,
} from '@material-ui/core';
import PropTypes from 'prop-types';
import {withTranslation} from 'react-i18next';
import {DropdownSelect, InfoIcon} from '../../../components';
import {
    getModeOptionString,
    GoogleMapsDistanceMatrixModeOption,
} from "../../../services/enums/GoolgeMapsDistanceMatrixHelper";
import DateFnsUtils from "@date-io/date-fns";
import {MuiPickersUtilsProvider, DateTimePicker} from "@material-ui/pickers";
import deLocale from 'date-fns/locale/de';
import NumberInput from "../../BasicComponents/NumberInput";

function ConfigureOptimizerAlgorithmDialog(props) {
    const {
        configureOptimizerAlgorithmDialogState,
        onClose,
        onSave,
        t,
        config
    } = props;

    const [editedConfiguration, setEditedConfiguration] = useState(config);

    const isDepartureTimeValid = editedConfiguration.departureTime >= new Date();
    const isShopCapacityValid = editedConfiguration.shopCapacityDefaultValue && editedConfiguration.shopCapacityDefaultValue <= editedConfiguration.vehicleCapacity;
    const isChestCapacityValid = editedConfiguration.chestCapacityDefaultValue && editedConfiguration.chestCapacityDefaultValue <= editedConfiguration.vehicleCapacity;
    const isInputValid = isDepartureTimeValid && editedConfiguration.useStopCapacitiesFromDb ? true : isShopCapacityValid && isChestCapacityValid;
    const handleSave = () => {
        onSave(editedConfiguration);
    };

    const handleReset = () => {
        setEditedConfiguration(config);
    }

    const handleClose = () => {
        onClose();
    };

    const handleSliderChange = (key) => (e, newValue) => {
        setEditedConfiguration((prev) => ({
            ...prev,
            [key]: newValue,
        }));
    };

    const handleGoogleMatrixModeChange = (mode) => {
        setEditedConfiguration((prev) => ({
            ...prev,
            travelMode: mode
        }))
    }

    return (
        <Dialog open={configureOptimizerAlgorithmDialogState.open} onClose={handleClose}>
            <DialogTitle>{t('optimizationConfiguration')}</DialogTitle>
            <DialogContent>
                {/* Vehicle Capacity */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('vehicleTemplateCapacity')}: {editedConfiguration.vehicleCapacity}
                    </Typography>
                    <InfoIcon descriptionKey={'40'}/>
                </div>
                <Slider
                    value={editedConfiguration.vehicleCapacity}
                    onChange={handleSliderChange('vehicleCapacity')}
                    min={1}
                    max={30}
                    step={1}
                    valueLabelDisplay="auto"
                />

                {/* Stop Capacities */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('stopCapacitySource')}:
                    </Typography>
                    <InfoIcon descriptionKey={'90'}/>
                </div>
                <RadioGroup
                    style={{marginBottom: 8}}
                    row
                    value={editedConfiguration.useStopCapacitiesFromDb ? 'database' : 'fixed'}
                    onChange={(event) =>
                        setEditedConfiguration((prev) => ({
                            ...prev,
                            useStopCapacitiesFromDb: event.target.value === 'database',
                        }))}
                >
                    <FormControlLabel
                        value="database"
                        control={<Radio color="primary"/>}
                        label={t('database')}
                    />
                    <FormControlLabel
                        value="fixed"
                        control={<Radio color="primary"/>}
                        label={t('fixedCapacities')}
                    />
                </RadioGroup>

                {!editedConfiguration.useStopCapacitiesFromDb && (
                    <div style={{display: 'flex', gap: 12, marginBottom: 20}}>
                        <NumberInput
                            id="shopCapacity"
                            name={t('shopCapacity')}
                            setValue={(value) =>
                                setEditedConfiguration((prev) => ({
                                    ...prev,
                                    shopCapacityDefaultValue: value,
                                }))
                            }
                            value={
                                editedConfiguration.shopCapacityDefaultValue
                            }
                            error={!isShopCapacityValid}
                        />
                        <NumberInput
                            id="chestCapacity"
                            name={t('chestCapacity')}
                            setValue={(value) =>
                                setEditedConfiguration((prev) => ({
                                    ...prev,
                                    chestCapacityDefaultValue: value,
                                }))}
                            value={
                                editedConfiguration.chestCapacityDefaultValue
                            }
                            error={!isChestCapacityValid}
                        />
                    </div>
                )}

                {/* Tour Count */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8, marginTop: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('tourCount')}: {editedConfiguration.tourCount}
                    </Typography>
                    <InfoIcon descriptionKey={'41'}/>
                </div>
                <Slider
                    value={editedConfiguration.tourCount}
                    onChange={handleSliderChange('tourCount')}
                    min={0}
                    max={20}
                    step={1}
                    valueLabelDisplay="auto"
                />

                {/* TimeLimit */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('timeLimit')}: {editedConfiguration.timeLimit}
                    </Typography>
                    <InfoIcon descriptionKey={'42'}/>
                </div>
                <Slider
                    value={editedConfiguration.timeLimit}
                    onChange={handleSliderChange('timeLimit')}
                    min={30}
                    max={1800}
                    step={30}
                    valueLabelDisplay="auto"
                />

                {/* use Pickup and Delivery Pair */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('useRelations')}: {editedConfiguration.useRelation ? t('yes') : t('no')}
                    </Typography>
                    <InfoIcon descriptionKey={'70'}/>
                    <Checkbox
                        checked={editedConfiguration.useRelation}
                        color="primary"
                        onChange={(event) =>
                            setEditedConfiguration((prev) => ({
                                ...prev,
                                useRelation: event.target.checked,
                            }))}
                        value={editedConfiguration.useRelation}
                    />
                </div>
                <div style={{display: 'flex', alignItems: 'baseline', marginBottom: 8, flexDirection: 'column'}}>
                    <DropdownSelect
                        name={t('modeOption')}
                        options={Object.values(GoogleMapsDistanceMatrixModeOption).map(val => ({
                            value: val,
                            name: t(getModeOptionString(val))
                        }))}
                        required
                        setValue={handleGoogleMatrixModeChange}
                        sort
                        value={editedConfiguration.travelMode}
                    />
                    <MuiPickersUtilsProvider utils={DateFnsUtils} locale={deLocale}>
                        <DateTimePicker
                            ampm={false}
                            label={t('time')}
                            value={editedConfiguration.departureTime}
                            format="d. MMMM, HH:mm"
                            error={!isDepartureTimeValid}
                            onChange={(value) => {
                                setEditedConfiguration((prev) => ({
                                    ...prev,
                                    departureTime: new Date(value)
                                }));
                            }}
                            margin="normal"
                        />
                    </MuiPickersUtilsProvider>
                </div>


                {/* use Manhattan Distance */}
                <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                    <Typography style={{marginRight: 8}}>
                        {t('useManhattanDistance')}: {editedConfiguration.useManhattanDistance ? t('yes') : t('no')}
                    </Typography>
                    <InfoIcon descriptionKey={'80'}/>
                    <Checkbox
                        checked={editedConfiguration.useManhattanDistance}
                        color="primary"
                        onChange={(event) =>
                            setEditedConfiguration((prev) => ({
                                ...prev,
                                useManhattanDistance: event.target.checked,
                            }))}
                        value={editedConfiguration.useManhattanDistance}
                    />
                </div>

                {/* Vehicle Speed */}
                {editedConfiguration.useManhattanDistance &&
                    <>
                        <div style={{display: 'flex', alignItems: 'center', marginBottom: 8}}>
                            <Typography style={{marginRight: 8}}>
                                {t('vehicleAverageSpeedKmh')}: {editedConfiguration.vehicleSpeed}
                            </Typography>
                            <InfoIcon descriptionKey={'20'}/>
                        </div>
                        <Slider
                            value={editedConfiguration.vehicleSpeed}
                            onChange={handleSliderChange('vehicleSpeed')}
                            min={1}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                        />
                    </>
                }

            </DialogContent>
            <DialogActions>
                <Button onClick={handleReset} color="default">
                    {t('reset')}
                </Button>
                <Button onClick={handleClose} color="default">
                    {t('cancel')}
                </Button>
                <Button
                    onClick={handleSave}
                    color="primary"
                    disabled={!isInputValid}
                >
                    {t('ok')}
                </Button>
            </DialogActions>
        </Dialog>
    )
        ;
}

ConfigureOptimizerAlgorithmDialog.propTypes = {
    configureOptimizerAlgorithmDialogState: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    t: PropTypes.func.isRequired,
    config: PropTypes.object.isRequired,
    stopsCount: PropTypes.number.isRequired,
};

export default withTranslation()(ConfigureOptimizerAlgorithmDialog);
